import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { PartyPopper } from "lucide-react";
import { api } from "@/lib/api";

/**
 * SpotlightTour — darkens the UI, highlights one element per step
 * (found by data-testid), and shows a positioned info bubble with
 * Back / Next / Skip and a progress indicator. Progress persists.
 */
export default function SpotlightTour({ steps, startIndex = 0, onExit }) {
  const navigate = useNavigate();
  const [idx, setIdx] = useState(Math.min(startIndex, Math.max(steps.length - 1, 0)));
  const [rect, setRect] = useState(null);
  const [finished, setFinished] = useState(false);

  const step = steps[idx];

  useEffect(() => {
    if (!step) return;
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (!el) {
      // Target missing (locked feature / collapsed UI) — auto-advance
      if (idx < steps.length - 1) setIdx(idx + 1);
      else setFinished(true);
      return;
    }
    el.scrollIntoView({ block: "center", behavior: "auto" });
    let raf;
    const track = () => {
      const r = el.getBoundingClientRect();
      setRect((p) => (!p || Math.abs(p.top - r.top) > 1 || Math.abs(p.left - r.left) > 1 ||
                      Math.abs(p.width - r.width) > 1 || Math.abs(p.height - r.height) > 1
        ? { top: r.top, left: r.left, width: r.width, height: r.height } : p));
      raf = requestAnimationFrame(track);
    };
    raf = requestAnimationFrame(track);
    return () => cancelAnimationFrame(raf);
  }, [idx, step, steps.length]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") skip(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persist = (n) => api.post("/tour/progress", { step: n }).catch(() => {});

  const next = () => {
    if (idx < steps.length - 1) { setIdx(idx + 1); persist(idx + 1); }
    else { setFinished(true); api.post("/tour/complete").catch(() => {}); }
  };
  const back = () => { if (idx > 0) { setIdx(idx - 1); persist(idx - 1); } };
  const skip = () => { api.post("/tour/skip").catch(() => {}); onExit(); };

  if (!steps.length) { onExit(); return null; }

  if (finished) {
    return (
      <div className="fixed inset-0 z-[96] flex items-center justify-center p-4" data-testid="tour-finished">
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
        <div className="relative w-full max-w-md rounded-2xl border border-line bg-bg-surface p-8 text-center shadow-2xl animate-fade-up">
          <PartyPopper className="h-10 w-10 text-brand-amber mx-auto mb-4" />
          <h2 className="font-display text-2xl font-bold text-ink-primary">You're Ready!</h2>
          <p className="text-sm text-ink-secondary mt-2">You've completed the BroadcastFlow AI tour.</p>
          <div className="mt-6 space-y-2.5">
            <button onClick={() => { onExit(); navigate("/dashboard"); }} data-testid="tour-finish-dashboard"
              className="w-full rounded-xl bg-brand-amber px-5 py-3 text-sm font-bold text-black hover:bg-brand-amber/90 transition">
              Go to Dashboard
            </button>
            <button data-testid="tour-finish-checklist"
              onClick={() => {
                onExit(); navigate("/dashboard");
                setTimeout(() => document.querySelector('[data-testid="setup-checklist-card"]')?.scrollIntoView({ block: "center", behavior: "smooth" }), 600);
              }}
              className="w-full rounded-xl border border-line bg-bg-elevated px-5 py-3 text-sm font-bold text-ink-primary hover:border-brand-amber/50 transition">
              Complete Setup Checklist
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!rect) return <div className="fixed inset-0 z-[96] bg-black/60" data-testid="tour-loading" />;

  const pad = 6;
  const hl = { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 };
  const bubbleW = 330;
  let bLeft = hl.left + hl.width + 16;
  if (bLeft + bubbleW > window.innerWidth - 12) bLeft = Math.max(12, hl.left - bubbleW - 16);
  const bTop = Math.max(12, Math.min(hl.top, window.innerHeight - 250));

  return (
    <div className="fixed inset-0 z-[96]" data-testid="tour-spotlight">
      {/* Highlight ring — the giant box-shadow darkens everything else */}
      <div className="absolute rounded-lg border-2 border-brand-amber pointer-events-none transition-all duration-300"
        data-testid="tour-highlight"
        style={{ top: hl.top, left: hl.left, width: hl.width, height: hl.height,
                 boxShadow: "0 0 0 9999px rgba(5,6,10,0.8), 0 0 24px rgba(251,146,60,0.35)" }} />
      {/* Info bubble */}
      <div className="absolute w-[330px] max-w-[calc(100vw-24px)] rounded-xl border border-line bg-bg-surface p-5 shadow-2xl animate-fade-up"
        data-testid="tour-bubble" style={{ top: bTop, left: bLeft }}>
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber mb-2" data-testid="tour-step-progress">
          Step {idx + 1} of {steps.length}
        </div>
        <div className="font-display text-lg font-bold text-ink-primary" data-testid="tour-step-title">{step.title}</div>
        <p className="text-xs text-ink-secondary mt-1.5 leading-relaxed" data-testid="tour-step-body">{step.body}</p>
        <div className="mt-2 flex gap-1">
          {steps.map((_, i) => (
            <span key={i} className={`h-1 flex-1 rounded-full ${i <= idx ? "bg-brand-amber" : "bg-bg-elevated"}`} />
          ))}
        </div>
        <div className="mt-4 flex items-center justify-between">
          <button onClick={skip} data-testid="tour-skip-btn"
            className="text-[11px] font-semibold text-ink-muted hover:text-ink-primary transition">Skip Tour</button>
          <div className="flex gap-2">
            {idx > 0 && (
              <button onClick={back} data-testid="tour-back-btn"
                className="rounded-lg border border-line px-4 py-2 text-xs font-bold text-ink-secondary hover:text-ink-primary transition">Back</button>
            )}
            <button onClick={next} data-testid="tour-next-btn"
              className="rounded-lg bg-brand-amber px-5 py-2 text-xs font-bold text-black hover:bg-brand-amber/90 transition">
              {idx === steps.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
